import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject, debounceTime, of, switchMap, takeUntil, tap } from 'rxjs';
import { FilterOperators } from 'src/app/common/enums';
import { PropertyFilterOperator } from 'src/app/common/interfaces/property-filter-operator';
import { buildFilterArray } from 'src/app/common/utils';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { Filter } from 'src/app/models/common/filter';
import { DisableErrorStateMatcher } from 'src/app/shared/error-state-matchers/disable-error-state-matcher';

@Component({
  selector: 'app-e-document-bufor-filters',
  templateUrl: './e-document-bufor-filters.component.html',
  styleUrl: './e-document-bufor-filters.component.scss',
  standalone: false
})
export class EDocumentBuforFiltersComponent {
  private readonly timeBetweenInput = 300;

  @Input() formGroup: FormGroup;
  @Input() hasLegalization: boolean;

  companies$: Observable<DictionaryItem[]>;
  eDocumentTypes$: Observable<DictionaryItem[]> = this.dictionaryService.getEDocumentTypes();
  eDocumentParts$: Observable<DictionaryItem[]> = this.dictionaryService.getEDocumentParts();

  private readonly unsubscribe$ = new Subject<void>();
  public readonly matcher = new DisableErrorStateMatcher();

  @Output() filterGridData = new EventEmitter<Filter[]>();

  public static readonly operatorsMap: Map<string, PropertyFilterOperator> = new Map<string, PropertyFilterOperator>([
    ['companyId', { property: 'CompanyId', operator: FilterOperators.Equal }],
    ['addedDateFrom', { property: 'AddedDate', operator: FilterOperators.DateGreaterThanOrEqual }],
    ['addedDateTo', { property: 'AddedDate', operator: FilterOperators.DateLessThanOrEqual }],
    ['validUntilDateFrom', { property: 'ValidUntilDate', operator: FilterOperators.DateGreaterThanOrEqual }],
    ['validUntilDateTo', { property: 'ValidUntilDate', operator: FilterOperators.DateLessThanOrEqual }],
    ['lastName', { property: 'LastName', operator: FilterOperators.Contains }],
    ['firstName', { property: 'FirstName', operator: FilterOperators.Contains }],
    ['pesel', { property: 'Pesel', operator: FilterOperators.Contains }],
    ['part', { property: 'DocumentPart', operator: FilterOperators.Contains }],
    ['documentId', { property: 'DocumentId', operator: FilterOperators.Contains }],
    ['document', { property: 'DocumentType', operator: FilterOperators.Contains }],
    ['passport', { property: 'Passport', operator: FilterOperators.Contains }],
  ]);

  constructor(
    private dictionaryService: DictionaryService
  ) { }

  get company(): FormControl {
    return this.formGroup.get('company') as FormControl;
  }

  ngOnInit(): void {
    this.onCompanyChange();

    this.formGroup.valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(1000)).subscribe(() => {

      if (this.formGroup.invalid) {
        return;
      }

      this.filterGridData.emit(buildFilterArray(this.formGroup, EDocumentBuforFiltersComponent.operatorsMap));
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  resetFilters(): void {
    this.formGroup.reset();
  }

  displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  private onCompanyChange() {
    this.companies$ = this.company.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      tap((value: any) => value?.Id && this.formGroup.patchValue({ companyId: value.Id })),
      switchMap((value: string) => (value ? this.dictionaryService.getCompanies(value) : of([]))),
    );
  }
}
